import React from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { Trash } from 'react-bootstrap-icons';
import { useSelector } from 'react-redux';
import {
  createArtworkWorkspaceBatchJob,
  getArtworkWorkspaceBatchJob,
  getArtworkWorkspaceBatchJobResults,
  retryArtworkWorkspaceBatchJobFailures,
} from '../../store/artworks/artworks.api';
import { ArtworkWorkspaceBatchJob, ArtworkWorkspaceBatchResultsPage } from '../../types/artworkWorkspace';
import workspaceTheme from '../artwork-workspace/workspaceTheme.module.css';
import { trackWorkspaceEvent } from '../../utils/workspaceTelemetry';
import { ApiClient } from '../../utils/apiClient';
import { AppState } from '../../store/root.reducer';
import User from '../../models/user.model';
import { getNormalizedUserRoles } from '../../utils/userAccess';
import { getUsersRequest } from '../../pages/admin/store/admin.api';

const BASE_API = process.env.REACT_APP_BASE_API || "";
const apiClient = ApiClient(BASE_API);

const isTerminalJobStatus = (status?: string | null) =>
  status === 'completed' || status === 'completed_with_errors' || status === 'failed';

type PreviewItem = { name: string; preview: string; type: string };
type BatchFeedback = { variant: "success" | "danger"; message: string } | null;

export type SpatialBatchPlacementInput = {
  sourceObjectId: string;
  sourceAssetKey: string;
  displayFilename: string;
  mediaType: "image" | "video" | "3dmodel";
  title: string;
  importState: "PENDING" | "QUEUED" | "IMPORTED" | "FAILED" | "SKIPPED";
  matchState?: "PENDING" | "AUTO_MATCHED" | "MANUALLY_MATCHED" | "AMBIGUOUS" | "MISSING";
};

type BatchCreatingArtworksProps = {
  spatialMigrationRunId?: string;
  spatialPlacements?: SpatialBatchPlacementInput[];
  targetUserId?: string;
  targetUserLabel?: string;
  lockTargetUser?: boolean;
  defaultArtistName?: string;
  onJobChange?: (job: ArtworkWorkspaceBatchJob) => void;
};

export const BatchCreatingArtworks = ({
  spatialMigrationRunId,
  spatialPlacements = [],
  targetUserId,
  targetUserLabel,
  lockTargetUser = false,
  defaultArtistName = "",
  onJobChange,
}: BatchCreatingArtworksProps = {}) => {
  const imageFile = React.useRef<HTMLInputElement | null>(null);
  const excelFile = React.useRef<HTMLInputElement | null>(null);
  const uploadAbortController = React.useRef<AbortController | null>(null);
  const currentUser = useSelector((state: AppState) => state.auth.user as User | null);
  const roles = getNormalizedUserRoles(currentUser);
  const isStrictAdmin = roles.includes("ADMIN") || roles.includes("SUPERADMIN");

  const [loading, setLoading] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState<number | null>(null);
  const [metadata, setMetadata] = React.useState<File | null>(null);
  const [images, setImages] = React.useState<File[]>([]);
  const [imagePreview, setImagePreview] = React.useState<PreviewItem[]>([]);
  const [job, setJob] = React.useState<ArtworkWorkspaceBatchJob | null>(null);
  const [results, setResults] = React.useState<ArtworkWorkspaceBatchResultsPage | null>(null);
  const [feedback, setFeedback] = React.useState<BatchFeedback>(null);
  const [downloadingTemplate, setDownloadingTemplate] = React.useState(false);
  const [availableUsers, setAvailableUsers] = React.useState<User[]>([]);
  const [selectedTargetUserId, setSelectedTargetUserId] = React.useState(targetUserId || "");
  const [batchDefaultArtistName, setBatchDefaultArtistName] = React.useState(defaultArtistName);
  const [spatialManualMatches, setSpatialManualMatches] = React.useState<Record<string, string>>({});
  const [showAllMediaFiles, setShowAllMediaFiles] = React.useState(false);
  const [spatialMediaPage, setSpatialMediaPage] = React.useState(1);
  const [showAutomaticMatches, setShowAutomaticMatches] = React.useState(false);
  const [automaticMatchQuery, setAutomaticMatchQuery] = React.useState("");
  const [automaticMatchPage, setAutomaticMatchPage] = React.useState(1);
  const [spatialResolverQuery, setSpatialResolverQuery] = React.useState("");
  const [spatialResolverPage, setSpatialResolverPage] = React.useState(1);

  React.useEffect(() => {
    setSelectedTargetUserId(targetUserId || "");
  }, [targetUserId]);

  React.useEffect(() => {
    setBatchDefaultArtistName(defaultArtistName);
  }, [defaultArtistName]);

  React.useEffect(() => () => {
    uploadAbortController.current?.abort();
  }, []);

  React.useEffect(() => {
    let active = true;
    if (!isStrictAdmin || lockTargetUser) {
      return () => { active = false; };
    }
    void getUsersRequest().then((response) => {
      if (!active || !response.data?.status) return;
      setAvailableUsers(response.data?.users || response.data?.data?.items || []);
    });
    return () => { active = false; };
  }, [isStrictAdmin, lockTargetUser]);

  const resetInputs = () => {
    setMetadata(null);
    setImages([]);
    setImagePreview([]);
    setSpatialManualMatches({});
    setShowAllMediaFiles(false);
    setSpatialMediaPage(1);
    setShowAutomaticMatches(false);
    setAutomaticMatchQuery("");
    setAutomaticMatchPage(1);
    setSpatialResolverQuery("");
    setSpatialResolverPage(1);
    if (imageFile.current) {
      imageFile.current.value = "";
    }
    if (excelFile.current) {
      excelFile.current.value = "";
    }
  };

  const readFileAsDataURL = async (file: File) => {
    const result = await new Promise<string | ArrayBuffer | null>((resolve) => {
      const fileReader = new FileReader();
      fileReader.onload = () => resolve(fileReader.result);
      fileReader.readAsDataURL(file);
    });
    return String(result || "");
  };

  const getArtworkType = (file: File) => {
    const mimeType = file.type;
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'sound';
    const fileExt = file.name.split(".").pop()?.toLowerCase();
    if (fileExt && ['gltf', 'glb'].includes(fileExt)) return '3dmodel';
    return 'unsupported';
  };

  const onMetadataChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMetadata(e.target.files?.[0] || null);
  };

  const onChangeImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextImages = Array.from(e.target.files || []);
    const previews: PreviewItem[] = [];
    if (!spatialMigrationRunId) {
      for (const file of nextImages) {
        const type = getArtworkType(file);
        let preview = '/assets/3d-model.png';
        if (type === 'image' || type === 'video') {
          preview = await readFileAsDataURL(file);
        } else if (type === 'sound') {
          preview = '/assets/speaker.png';
        }
        previews.push({ name: file.name, preview, type });
      }
    }
    setImages(nextImages);
    setImagePreview(previews);
    setSpatialManualMatches({});
    setShowAllMediaFiles(false);
    setSpatialMediaPage(1);
    setShowAutomaticMatches(false);
    setAutomaticMatchQuery("");
    setAutomaticMatchPage(1);
    setSpatialResolverQuery("");
    setSpatialResolverPage(1);
  };

  const onRemoveImage = (imageToRemove: File) => {
    const nextImages = images.filter((file) => file !== imageToRemove);
    const nextPreview = imagePreview.filter((item) => item.name !== imageToRemove.name);
    setImages(nextImages);
    setImagePreview(nextPreview);
    setSpatialManualMatches({});
    setSpatialMediaPage(1);
    if (nextImages.length === 0 && imageFile.current) {
      imageFile.current.value = "";
    }
  };

  const normalizeSpatialFilename = React.useCallback((value: string) => value
    .normalize("NFKC")
    .replace(/\\/g, "/")
    .split("/")
    .pop()!
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase(), []);

  const eligibleSpatialPlacements = React.useMemo(() => spatialPlacements.filter((placement) =>
      placement.importState !== "IMPORTED"
      && placement.importState !== "QUEUED"
      && placement.importState !== "SKIPPED"), [spatialPlacements]);

  const spatialMatchAnalysis = React.useMemo(() => {
    if (!spatialMigrationRunId || images.length === 0) {
      return { automaticMatches: [], resolvers: [] };
    }
    const sourceAssetsByIdentity = new Map<string, Set<string>>();
    const identitiesBySourceAsset = new Map<string, Set<string>>();
    for (const placement of eligibleSpatialPlacements) {
      const identity = `${placement.mediaType}:${normalizeSpatialFilename(placement.displayFilename)}`;
      const assets = sourceAssetsByIdentity.get(identity) || new Set<string>();
      assets.add(placement.sourceAssetKey);
      sourceAssetsByIdentity.set(identity, assets);
      const identities = identitiesBySourceAsset.get(placement.sourceAssetKey) || new Set<string>();
      identities.add(identity);
      identitiesBySourceAsset.set(placement.sourceAssetKey, identities);
    }

    const automaticMatches: Array<{
      placement: SpatialBatchPlacementInput;
      file: File;
      type: string;
    }> = [];
    const resolvers: Array<{
      placement: SpatialBatchPlacementInput;
      compatibleFiles: Array<{ file: File; index: number; type: string }>;
    }> = [];

    eligibleSpatialPlacements.forEach((placement) => {
      const compatibleFiles = images
        .map((file, index) => ({ file, index, type: getArtworkType(file) }))
        .filter((item) => item.type === placement.mediaType);
      const identity = `${placement.mediaType}:${normalizeSpatialFilename(placement.displayFilename)}`;
      const exactFiles = compatibleFiles.filter((item) =>
        normalizeSpatialFilename(item.file.name) === normalizeSpatialFilename(placement.displayFilename));
      const sourceCollision = (sourceAssetsByIdentity.get(identity)?.size || 0) > 1
        || (identitiesBySourceAsset.get(placement.sourceAssetKey)?.size || 0) > 1;
      const needsManualChoice = sourceCollision
        || exactFiles.length !== 1;
      if (needsManualChoice) {
        resolvers.push({ placement, compatibleFiles });
      } else {
        automaticMatches.push({
          placement,
          file: exactFiles[0].file,
          type: exactFiles[0].type,
        });
      }
    });

    return { automaticMatches, resolvers };
  }, [eligibleSpatialPlacements, images, normalizeSpatialFilename, spatialMigrationRunId]);
  const spatialResolvers = spatialMatchAnalysis.resolvers;
  const automaticSpatialMatches = spatialMatchAnalysis.automaticMatches;

  const filteredAutomaticMatches = React.useMemo(() => {
    const query = automaticMatchQuery.trim().toLowerCase();
    if (!query) return automaticSpatialMatches;
    return automaticSpatialMatches.filter(({ placement, file }) =>
      placement.title.toLowerCase().includes(query)
      || placement.displayFilename.toLowerCase().includes(query)
      || file.name.toLowerCase().includes(query));
  }, [automaticMatchQuery, automaticSpatialMatches]);
  const automaticMatchPageSize = 20;
  const automaticMatchPageCount = Math.max(1, Math.ceil(filteredAutomaticMatches.length / automaticMatchPageSize));
  const visibleAutomaticMatches = filteredAutomaticMatches.slice(
    (automaticMatchPage - 1) * automaticMatchPageSize,
    automaticMatchPage * automaticMatchPageSize,
  );

  const filteredSpatialResolvers = React.useMemo(() => {
    const query = spatialResolverQuery.trim().toLowerCase();
    if (!query) return spatialResolvers;
    return spatialResolvers.filter(({ placement }) =>
      placement.title.toLowerCase().includes(query)
      || placement.displayFilename.toLowerCase().includes(query));
  }, [spatialResolverQuery, spatialResolvers]);
  const spatialResolverPageSize = 20;
  const spatialResolverPageCount = Math.max(1, Math.ceil(filteredSpatialResolvers.length / spatialResolverPageSize));
  const visibleSpatialResolvers = filteredSpatialResolvers.slice(
    (spatialResolverPage - 1) * spatialResolverPageSize,
    spatialResolverPage * spatialResolverPageSize,
  );
  const autoMatchedSpatialCount = automaticSpatialMatches.length;
  const spatialMediaPageSize = 20;
  const spatialMediaPageCount = Math.max(1, Math.ceil(images.length / spatialMediaPageSize));
  const visibleSpatialMedia = images.slice(
    (spatialMediaPage - 1) * spatialMediaPageSize,
    spatialMediaPage * spatialMediaPageSize,
  );

  React.useEffect(() => {
    setSpatialResolverPage(1);
  }, [spatialResolverQuery, spatialResolvers.length]);

  React.useEffect(() => {
    setAutomaticMatchPage(1);
  }, [automaticMatchQuery, automaticSpatialMatches.length]);

  const loadResults = React.useCallback(async (jobId: string) => {
    const nextResults = await getArtworkWorkspaceBatchJobResults(jobId, 1, 50);
    setResults(nextResults);
  }, []);

  React.useEffect(() => {
    if (!job?.jobId || isTerminalJobStatus(job.status)) {
      return;
    }

    const timer = window.setInterval(async () => {
      try {
        const nextJob = await getArtworkWorkspaceBatchJob(job.jobId);
        setJob(nextJob);
        onJobChange?.(nextJob);
        await loadResults(nextJob.jobId);
      } catch (_error) {
        // keep last known state
      }
    }, 3000);

    return () => window.clearInterval(timer);
  }, [job?.jobId, job?.status, loadResults, onJobChange]);

  const submit = async () => {
    if (!metadata) {
      setFeedback({ variant: 'danger', message: 'Select an XLSX workbook first.' });
      return;
    }
    if (images.length === 0) {
      setFeedback({ variant: 'danger', message: 'Select at least one media file.' });
      return;
    }
    const normalizedMediaNames = images.map((file) => file.name.trim().toLowerCase());
    if (new Set(normalizedMediaNames).size !== normalizedMediaNames.length) {
      setFeedback({
        variant: 'danger',
        message: 'Media filenames must be unique. Rename duplicates and update the workbook File Name values.',
      });
      return;
    }

    try {
      setLoading(true);
      setUploadProgress(0);
      const controller = new AbortController();
      uploadAbortController.current = controller;
      const nextJob = await createArtworkWorkspaceBatchJob(metadata, images, {
        ...(selectedTargetUserId ? { targetUserId: selectedTargetUserId } : {}),
        ...(batchDefaultArtistName.trim()
          ? { defaultArtistName: batchDefaultArtistName.trim() }
          : {}),
        ...(spatialMigrationRunId ? { spatialMigrationRunId } : {}),
        ...(spatialMigrationRunId && Object.keys(spatialManualMatches).length > 0
          ? {
              spatialMatches: Object.entries(spatialManualMatches).map(([sourceObjectId, uploadId]) => ({
                sourceObjectId,
                uploadId,
              })),
            }
          : {}),
        onUploadProgress: setUploadProgress,
        signal: controller.signal,
      });
      setJob(nextJob);
      onJobChange?.(nextJob);
      await loadResults(nextJob.jobId);
      setFeedback({ variant: 'success', message: 'Batch job created.' });
      trackWorkspaceEvent('batch_job_created', { jobId: nextJob.jobId, files: images.length });
      resetInputs();
    } catch (error: any) {
      setFeedback({ variant: 'danger', message: error?.response?.data?.error?.message || 'Cannot create artworks in batch' });
    } finally {
      uploadAbortController.current = null;
      setLoading(false);
      setUploadProgress(null);
    }
  };

  const retryFailures = async () => {
    if (!job?.jobId) {
      return;
    }

    try {
      setLoading(true);
      const nextJob = await retryArtworkWorkspaceBatchJobFailures(job.jobId);
      setJob(nextJob);
      onJobChange?.(nextJob);
      await loadResults(nextJob.jobId);
      setFeedback({ variant: 'success', message: 'Retry job queued.' });
      trackWorkspaceEvent('batch_job_retry_queued', { jobId: nextJob.jobId });
    } catch (error: any) {
      setFeedback({ variant: 'danger', message: error?.response?.data?.error?.message || 'Retry failed' });
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      const response = await apiClient.downloadArtworkWorkspaceBatchTemplate();
      if (!response.status || !response.blob) {
        setFeedback({ variant: 'danger', message: response.error?.message || 'Template download failed' });
        return;
      }

      const url = window.URL.createObjectURL(response.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.fileName;
      link.click();
      window.URL.revokeObjectURL(url);
      setFeedback({ variant: 'success', message: `Downloaded ${response.fileName}.` });
    } catch (_error) {
      setFeedback({ variant: 'danger', message: 'Template download failed' });
    } finally {
      setDownloadingTemplate(false);
    }
  };

  return (
    <div className={`${workspaceTheme.detailSection} d-flex flex-column gap-3`}>
      {feedback ? (
        <Alert
          variant={feedback.variant}
          className={`${workspaceTheme.surfaceAlert} ${workspaceTheme.feedbackAlert} ${feedback.variant === "success" ? workspaceTheme.feedbackSuccess : workspaceTheme.feedbackError}`}
          onClose={() => setFeedback(null)}
          dismissible
        >
          {feedback.message}
        </Alert>
      ) : null}

      <div className={workspaceTheme.toolbar}>
        <div>
          <h3 className="mb-1">Batch Import</h3>
          <div className={workspaceTheme.accessNote}>
            {spatialMigrationRunId
              ? "Upload one batch import workbook and the Spatial media files. Exact compatible filenames are matched to selected scene placements."
              : "Upload one workbook and its matching media files to create artworks in one queued job."}
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Button
            variant="light"
            className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary}`}
            onClick={downloadTemplate}
            disabled={downloadingTemplate}
          >
            {downloadingTemplate ? "Downloading…" : "Download Template"}
          </Button>
        </div>
      </div>

      <div>
        <div className="d-flex justify-content-between align-items-start gap-3 mt-1 mb-3">
          <div>
            <h4 className="mb-1">Batch Inputs</h4>
          </div>
        </div>

        {lockTargetUser && targetUserId ? (
          <div className="mb-3">
            <div className="small text-uppercase fw-bold">Artwork owner</div>
            <strong>{targetUserLabel || targetUserId}</strong>
            <div className={workspaceTheme.accessNote}>Customer ownership is locked by this migration run.</div>
          </div>
        ) : isStrictAdmin ? (
          <Form.Group className="mb-3" controlId="batch-artwork-owner">
            <Form.Label>Artwork owner</Form.Label>
            <Form.Select
              value={selectedTargetUserId}
              onChange={(event) => setSelectedTargetUserId(event.target.value)}
            >
              <option value="">My account (default)</option>
              {availableUsers.map((user) => (
                <option key={String(user._id)} value={String(user._id)}>
                  {user.name || user.email || String(user._id)}
                  {user.email && user.name ? ` · ${user.email}` : ""}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        ) : null}

        <Form.Group className="mb-3" controlId="batch-default-artist-name">
          <Form.Label>Default artist name <span className={workspaceTheme.neutralText}>(optional)</span></Form.Label>
          <Form.Control
            type="text"
            maxLength={300}
            value={batchDefaultArtistName}
            onChange={(event) => setBatchDefaultArtistName(event.target.value)}
            placeholder={selectedTargetUserId ? "Unknown creator when blank" : "Your account name when blank"}
          />
          <Form.Text className={workspaceTheme.neutralText}>
            Used only when a workbook row has no Artist Name.
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3" controlId="batch-metadata-workbook">
          <Form.Label>Metadata workbook</Form.Label>
          <Form.Control type="file" accept='.xlsx' ref={excelFile} onChange={onMetadataChange} />
        </Form.Group>
        <Form.Group className="mb-3" controlId="batch-media-files">
          <Form.Label>Media files</Form.Label>
          <Form.Control type="file" multiple ref={imageFile} onChange={onChangeImage} />
        </Form.Group>

        {spatialMigrationRunId && images.length > 0 && (
          <div className={workspaceTheme.batchSelectionSummary} aria-live="polite">
            <div className={workspaceTheme.batchSelectionCounts}>
              <strong>{images.length} media {images.length === 1 ? "file" : "files"} selected</strong>
              {eligibleSpatialPlacements.length > 0 && (
                <>
                  <span>
                    {autoMatchedSpatialCount} scene {autoMatchedSpatialCount === 1 ? "file" : "files"} matched automatically
                  </span>
                  <span className={spatialResolvers.length > 0 ? workspaceTheme.batchNeedsAttention : undefined}>
                    {spatialResolvers.length} need attention
                  </span>
                </>
              )}
            </div>
            <div className="d-flex gap-2 flex-wrap">
              {autoMatchedSpatialCount > 0 && (
                <button
                  type="button"
                  className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
                  aria-expanded={showAutomaticMatches}
                  onClick={() => setShowAutomaticMatches((current) => !current)}
                >
                  {showAutomaticMatches
                    ? "Hide automatic matches"
                    : `Review automatic matches (${autoMatchedSpatialCount})`}
                </button>
              )}
              <button
                type="button"
                className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
                aria-expanded={showAllMediaFiles}
                onClick={() => setShowAllMediaFiles((current) => !current)}
              >
                {showAllMediaFiles ? "Hide selected files" : "Show selected files"}
              </button>
            </div>
          </div>
        )}

        {!spatialMigrationRunId && images.length > 0 && images.map((file, index) => {
          const preview = imagePreview.find((item) => item.name === file.name);
          return (
            <div key={`${file.name}-${index}`} className="row mb-2 align-items-center">
              <div className="col-3">
                {preview?.type === "image" && <img src={preview.preview} className={workspaceTheme.batchPreview} width={100} height={72} alt={`Preview of ${file.name}`} />}
                {preview?.type === "video" && <video src={preview.preview} className={workspaceTheme.batchPreview} width={100} height={72} muted aria-label={`Preview of ${file.name}`} />}
                {preview?.type === "sound" && <img src={preview.preview} className={workspaceTheme.batchPreview} width={100} height={72} alt={`Audio file ${file.name}`} />}
                {preview?.type === "3dmodel" && <img src={preview.preview} className={workspaceTheme.batchPreview} width={100} height={72} alt={`3D model file ${file.name}`} />}
              </div>
              <div className="col-7">{file.name}</div>
              <div className="col-2 text-end">
                <button
                  type="button"
                  className={`btn btn-sm btn-outline-light ${workspaceTheme.batchRemoveButton}`}
                  onClick={() => onRemoveImage(file)}
                  aria-label={`Remove ${file.name}`}
                >
                  <Trash aria-hidden="true" />
                </button>
              </div>
            </div>
          );
        })}

        {spatialMigrationRunId && showAutomaticMatches && autoMatchedSpatialCount > 0 && (
          <section className="mt-3" aria-labelledby="automatic-spatial-matches-heading">
            <h4 id="automatic-spatial-matches-heading" className="mb-1">Automatic matches</h4>
            <p className={`small ${workspaceTheme.neutralText}`}>
              Exact filename and media type matched these scene artworks to uploaded files.
            </p>
            {automaticSpatialMatches.length > 10 && (
              <Form.Group className="mb-3" controlId="automatic-spatial-match-search">
                <Form.Label>Search automatic matches</Form.Label>
                <Form.Control
                  type="search"
                  value={automaticMatchQuery}
                  onChange={(event) => setAutomaticMatchQuery(event.target.value)}
                  placeholder="Artwork title or filename"
                />
              </Form.Group>
            )}
            <Table responsive striped variant="dark" className={workspaceTheme.darkTable}>
              <caption className="visually-hidden">Automatically matched Spatial scene files</caption>
              <thead>
                <tr>
                  <th scope="col">Scene artwork</th>
                  <th scope="col">Matched uploaded file</th>
                  <th scope="col">Media</th>
                </tr>
              </thead>
              <tbody>
                {visibleAutomaticMatches.map(({ placement, file, type }) => (
                  <tr key={placement.sourceObjectId}>
                    <td><strong>{placement.title}</strong></td>
                    <td>{file.name}</td>
                    <td>{type === "3dmodel" ? "3D model" : type}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {automaticMatchPageCount > 1 && (
              <div className={workspaceTheme.batchResolverPagination} role="group" aria-label="Automatic match pages">
                <button
                  type="button"
                  className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
                  disabled={automaticMatchPage <= 1}
                  onClick={() => setAutomaticMatchPage((page) => Math.max(1, page - 1))}
                >
                  Previous
                </button>
                <span>Page {automaticMatchPage} of {automaticMatchPageCount}</span>
                <button
                  type="button"
                  className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
                  disabled={automaticMatchPage >= automaticMatchPageCount}
                  onClick={() => setAutomaticMatchPage((page) => Math.min(automaticMatchPageCount, page + 1))}
                >
                  Next
                </button>
              </div>
            )}
          </section>
        )}

        {spatialMigrationRunId && showAllMediaFiles && (
          <section className={workspaceTheme.batchFileList} aria-label="Selected media files">
            {visibleSpatialMedia.map((file, index) => {
              const absoluteIndex = (spatialMediaPage - 1) * spatialMediaPageSize + index;
              const type = getArtworkType(file);
              return (
                <div key={`${file.name}-${absoluteIndex}`} className={workspaceTheme.batchFileRow}>
                  <span>
                    <strong>{file.name}</strong>
                    <small className={workspaceTheme.neutralText}>
                      {type === "3dmodel" ? "3D model" : type}
                    </small>
                  </span>
                  <button
                    type="button"
                    className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
                    onClick={() => onRemoveImage(file)}
                    aria-label={`Remove ${file.name}`}
                  >
                    <Trash aria-hidden="true" />
                  </button>
                </div>
              );
            })}
            {spatialMediaPageCount > 1 && (
              <div className={workspaceTheme.batchResolverPagination} role="group" aria-label="Selected media file pages">
                <button
                  type="button"
                  className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
                  disabled={spatialMediaPage <= 1}
                  onClick={() => setSpatialMediaPage((page) => Math.max(1, page - 1))}
                >
                  Previous
                </button>
                <span>Page {spatialMediaPage} of {spatialMediaPageCount}</span>
                <button
                  type="button"
                  className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
                  disabled={spatialMediaPage >= spatialMediaPageCount}
                  onClick={() => setSpatialMediaPage((page) => Math.min(spatialMediaPageCount, page + 1))}
                >
                  Next
                </button>
              </div>
            )}
          </section>
        )}

        {spatialResolvers.length > 0 && (
          <section className="mt-4" aria-labelledby="spatial-file-matching-heading">
            <h4 id="spatial-file-matching-heading" className="mb-1">Resolve scene file matches</h4>
            <p className={`small ${workspaceTheme.neutralText}`}>
              Exact filename and media-type matches are automatic. Choose files only for the unresolved artworks below.
              {' '}The workbook File Name must match the selected uploaded file name.
            </p>
            {spatialResolvers.length > 10 && (
              <Form.Group className="mb-3" controlId="spatial-file-match-search">
                <Form.Label>Search unresolved matches</Form.Label>
                <Form.Control
                  type="search"
                  value={spatialResolverQuery}
                  onChange={(event) => setSpatialResolverQuery(event.target.value)}
                  placeholder="Artwork title or expected filename"
                />
              </Form.Group>
            )}
            <Table responsive striped variant="dark" className={workspaceTheme.darkTable}>
              <caption className="visually-hidden">Unresolved Spatial scene file matches</caption>
              <thead>
                <tr>
                  <th scope="col">Scene reference</th>
                  <th scope="col">Expected file</th>
                  <th scope="col">Media</th>
                  <th scope="col">Uploaded file</th>
                </tr>
              </thead>
              <tbody>
                {visibleSpatialResolvers.map(({ placement, compatibleFiles }) => {
                  const label = `${placement.title} (${placement.sourceObjectId})`;
                  return (
                    <tr key={placement.sourceObjectId}>
                      <td><strong>{placement.title}</strong></td>
                      <td>{placement.displayFilename}</td>
                      <td>{placement.mediaType === "3dmodel" ? "3D model" : placement.mediaType}</td>
                      <td>
                        <Form.Select
                          size="sm"
                          aria-label={`Media file for ${label}`}
                          value={spatialManualMatches[placement.sourceObjectId] || ""}
                          onChange={(event) => setSpatialManualMatches((current) => ({
                            ...current,
                            [placement.sourceObjectId]: event.target.value,
                          }))}
                          disabled={compatibleFiles.length === 0}
                        >
                          <option value="">
                            {compatibleFiles.length === 0 ? "No compatible uploaded file" : "Choose a file"}
                          </option>
                          {compatibleFiles.map(({ file, index }) => (
                            <option key={`${index}-${file.name}`} value={String(index)}>
                              {file.name}{images.filter((candidate) => candidate.name === file.name).length > 1 ? ` (#${index + 1})` : ""}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            {spatialResolverPageCount > 1 && (
              <div className={workspaceTheme.batchResolverPagination} aria-label="Unresolved match pages">
                <button
                  type="button"
                  className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
                  disabled={spatialResolverPage <= 1}
                  onClick={() => setSpatialResolverPage((page) => Math.max(1, page - 1))}
                >
                  Previous
                </button>
                <span>Page {spatialResolverPage} of {spatialResolverPageCount}</span>
                <button
                  type="button"
                  className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
                  disabled={spatialResolverPage >= spatialResolverPageCount}
                  onClick={() => setSpatialResolverPage((page) => Math.min(spatialResolverPageCount, page + 1))}
                >
                  Next
                </button>
              </div>
            )}
          </section>
        )}

        <div className="d-flex gap-2 mt-3">
          <Button
            variant="light"
            className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonPrimary}`}
            onClick={submit}
            disabled={loading}
          >
            {loading
              ? uploadProgress == null
                ? "Submitting…"
                : uploadProgress < 100
                  ? `Uploading ${uploadProgress}%`
                  : "Finalizing…"
              : "Create Batch Job"}
          </Button>
          {!spatialMigrationRunId && (job?.status === 'completed_with_errors' || job?.status === 'failed') ? (
            <Button
              variant="light"
              className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary}`}
              onClick={retryFailures}
              disabled={loading}
            >
              Retry Failed Rows
            </Button>
          ) : null}
        </div>

        {job ? (
          <div className="mt-4">
            <h4 className="mb-2">Current Job</h4>
            {!spatialMigrationRunId && (
              <div className={`small mb-2 ${workspaceTheme.neutralText}`}>Job ID: {job.jobId}</div>
            )}
            <div className="small mb-3">
              Status: {job.statusReason || job.status}
              {job.status === 'running' ? <Spinner animation="border" size="sm" className="ms-2" /> : null}
            </div>
            <div className="small mb-3">
              Total {job.counters.total}, validated {job.counters.validated}, created {job.counters.created}, failed validation {job.counters.failedValidation}, failed processing {job.counters.failedProcessing}
            </div>
          </div>
        ) : null}

        {results ? (
          <div className="mt-3">
            <h5 className="mb-2">Row Results</h5>
            <Table responsive striped variant="dark" className={workspaceTheme.darkTable}>
              <caption className="visually-hidden">Batch import row results</caption>
              <thead>
                <tr>
                  <th scope="col">Row</th>
                  <th scope="col">File</th>
                  <th scope="col">Status</th>
                  <th scope="col">Messages</th>
                </tr>
              </thead>
              <tbody>
                {results.items.map((item) => (
                  <tr key={`${item.rowIndex}-${item.fileName || 'row'}`}>
                    <td>{item.rowIndex}</td>
                    <td>{item.fileName || "-"}</td>
                    <td>{item.status}</td>
                    <td>{item.messages.join("; ") || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : null}
      </div>
    </div>
  )
}
